/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { TemplateRef } from '@angular/core';
import { Observable } from 'rxjs';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';

export type IAviDataGridRow = any;
export type TStringRow = Record<'value', string>;
export type TAviDataGridRowComparator<T = IAviDataGridRow> = (row1: T, row2: T) => number;

/**
 * A field column is shown when marked with true in the hash, hidden otherwise.
 * Keys of the map are field IDs.
 */
export type TAviDataGridColumnPresenceMap = Record<string, boolean>;

/**
 * Visibility for a field to decide the hide/show behavior of the corresponding column.
 */
export enum AviDataGridFieldVisibility {
    // always shown, can't be hidden
    MANDATORY = 'mandatory',
    // initially hidden
    OPTIONAL = 'optional',
    // initially shown
    // if no visibility is set for a field, it'll be treated as 'default' and is initially shown
    DEFAULT = 'default',
}

export interface IColumnFilterState {
    label: Observable<string>;
}

export interface IAviDataGridLayout {
    showFooter?: boolean;
    hideCheckboxes?: boolean;
    placeholderMessage?: string;
    withReordering?: boolean;
    hideSearch?: boolean;
    searchInputPlaceholder?: string;
    hidePagination?: boolean;
    expandGridByDefault?: boolean;
    hideRowsTotal?: boolean;
    compact?: boolean;
}

export interface IAviDataGridLabelTooltipOptions {
    fieldName: string;
    objectType: string;
}

/**
 * Interface to display an option dropdown filter.
 */
export interface IDropdownFilter {
    enum?: string;
    dropdownOptions?: IAviDropdownOption[];
    onSelect(value: string): void;
}

export interface IAviDataGridConfigField<T = IAviDataGridRow> {
    label: string;
    // To add information tooltip to the column label.
    labelTooltipOptions?: IAviDataGridLabelTooltipOptions;
    id: string;
    icon?: string;
    // needed to adjust sizes when icons appear a bit different with the same size
    iconSize?: string;
    // sortBy string: property name | sortBy function: comparator
    sortBy?: string | TAviDataGridRowComparator<T>;
    visibility?: AviDataGridFieldVisibility;
    templateRef?: TemplateRef<HTMLElement>;
    columnFilterTemplateRef?: TemplateRef<HTMLElement>;
    columnFilterState?: IColumnFilterState;
    columnDropdownFilter?: IDropdownFilter;
    expandedTemplateRef?: TemplateRef<HTMLElement>;
    // Optional data defined in the field and supplied to the field templateRef as let-data
    // templateContext
    data?: Record<string, any>;
    getTooltip?(row: T): string;
    transform?(row: T): string | number;
    expandedTransform?(row: T): string | number;
}

export interface IAviDataGridMultipleaction<T = IAviDataGridRow> {
    label: string;
    disabled?(rows: T[]): boolean;
    onClick(rows: T[]): void;
}

export interface IAviDataGridSingleaction<T = IAviDataGridRow> {
    label: string;
    shape?: string;
    onClick(row: T): void;
    disabled?(row: T): boolean;
    hidden?(row: T): boolean;
}

export interface IAviDataGridConfig<T = IAviDataGridRow> {
    fields: Array<IAviDataGridConfigField<T>>;
    layout?: IAviDataGridLayout;
    multipleactions?: Array<IAviDataGridMultipleaction<T>>;
    singleactions?: Array<IAviDataGridSingleaction<T>>;
    searchKeys?: string[],
    isExpandedContentColumnBased?: boolean;
    /**
     * Content shown under a row when the row's expand button is clicked.
     */
    expandedContentTemplateRef?: TemplateRef<HTMLElement>;
    // For the grid expander. The grid expander and row expanders can't be enabled at the same time.
    isGridExpandable?: boolean;
    rowClass?: (row: T) => string;
    // For row expanders.
    expanderDisabled?(row: T): boolean;
    /**
     * Only set this function if the row has a unique id value, e.g.:
     * getUniqueRowId(row) => row.id
     */
    getUniqueRowId?(row: T): string | number;
    rowSelectionDisabled?(row: T): boolean;
}

export interface IRowReorder<T = IAviDataGridRow> {
    row: T;
    currentIndex: number;
    previousIndex: number;
}
